/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useCallback, useEffect, useState } from "react";
import { List, TextField, Checkbox, Divider, Grid } from "@material-ui/core";
import { format, getUnixTime, isValid, startOfDay } from "date-fns";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { getHolidaysAsync, updateHolidaysAsync } from "../../services/HolidayService";
import { HolidayCard } from "./HolidayCard";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { EmptyPlaceholder } from "../../../../components";
import { ActionButtons, ProgressOverlay } from "../../../../components/Common";
import { ConfirmDialog } from "../../../../components/ConfirmDialog/ConfirmDialog";

/**
 * Method for creating a new holiday object.
 * @param {Date} startDate The date to start the holiday.
 * @param {Date} endDate The date to end te holiday.
 * @param {String} queueId The ID of the queue for the holiday.
 * @param {Boolean} isRepeated Whether or not the holiday is repeated annually.
 */
const createHoliday = (holidayName, startDate, endDate, queueId, isRepeated) => {
	const formatString = isRepeated ? "MM/dd" : "MM/dd/yyyy";
	const newHoliday = {
		holidayName,
		startDate: format(startDate, formatString),
		endDate: format(endDate, formatString),
		queueId,
	};
	if (!isRepeated) newHoliday.expireTime = getUnixTime(endDate) + 86400;
	return newHoliday;
};

/**
 * This page is used to display and edit the holidays for a queue.
 * @param {String} queueId The ID of the queue for the holidays.
 * @param {Function} showSnackbar function to show / hide snackbar
 * @param {Function} setIsLoading function to show / hide loading indicator.
 * @param {Function} setTabModified function to show prompt on tab change.
 */
const HolidaysTab = ({ queue, showSnackbar, setIsLoading, setTabModified }) => {
	const [holidaysToDisplay, setNewHolidaysToDisplay] = useState(undefined);
	const [holidayName, setHolidayName] = useState("");
	const [startDate, setStartDate] = useState(new Date());
	const [endDate, setEndDate] = useState(new Date());
	const [isRepeated, setRepeated] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [modifiedState, setModifiedState] = useState({});
	const [confirmPrompt, setConfirmPrompt] = useState(undefined);
	const [errors, setErrors] = useState({ startDate: !!startDate, endDate: !!endDate, holidayName: !!holidayName });
	const formatString = isRepeated ? "MM/dd" : "MM/dd/yyyy";

	const setHolidaysToDisplay = (holidays) => {
		setNewHolidaysToDisplay(holidays.sort((a, b) => (a.startDate > b.startDate ? 1 : -1)));
	};

	useEffect(() => {
		setTabModified(!!Object.keys(modifiedState).find((holiday) => modifiedState[holiday]) || editMode);
	}, [modifiedState, setTabModified, editMode]);

	useEffect(() => {
		const nameError =
			holidayName.length === 0 ||
			(holidaysToDisplay && holidaysToDisplay.filter((holiday) => holiday.holidayName === holidayName).length !== 0);
		setErrors((err) => ({ ...err, holidayName: nameError }));
	}, [holidayName, holidaysToDisplay]);

	useEffect(() => {
		const getHolidays = async () => {
			setIsLoading(true);
			const newHolidays = await getHolidaysAsync(queue.Id);
			setHolidaysToDisplay(newHolidays);
			setIsLoading(false);
		};
		getHolidays();
	}, [queue, setNewHolidaysToDisplay, setIsLoading]);

	const setNewEndDate = (date) => {
		const currentYear = new Date().getFullYear();
		let newEndDate = new Date(date);
		if (isValid(newEndDate) && isRepeated && newEndDate < startDate) {
			newEndDate.setFullYear(currentYear + 1);
		}
		setEndDate(newEndDate);
	};

	const handleItemEditState = useCallback((state, itemName) => {
		setModifiedState((cur) => ({ ...cur, [itemName]: state }));
	}, []);

	const startOfDayDate = startOfDay(new Date());

	const addHolidayButtonDisabled =
		errors.startDate ||
		errors.endDate ||
		errors.holidayName ||
		startDate > endDate ||
		(startDate < startOfDayDate && !isRepeated);

	return (
		<div style={{ width: "100%" }}>
			{confirmPrompt !== undefined && confirmPrompt}
			{holidaysToDisplay && (
				<div>
					<div className="col-12">
						<Grid container justifyContent="space-between" spacing={2} className="mt-3">
							<Grid item className="w-100 d-flex align-items-center justify-content-end flex-wrap">
								<ActionButtons
									showCancel
									showSave={editMode}
									onCancel={() => {
										setEndDate(new Date());
										setStartDate(new Date());
										setHolidayName("");
										setRepeated(false);
										setEditMode(!editMode);
									}}
									onSave={async () => {
										setIsLoading(true);
										const newHoliday = createHoliday(holidayName, startDate, endDate, queue.Id, isRepeated);
										await updateHolidaysAsync({ updateHoliday: newHoliday }, queue.Id);
										setHolidaysToDisplay([...holidaysToDisplay, newHoliday]);
										setStartDate(new Date());
										setEndDate(new Date());
										setRepeated(false);
										setHolidayName("");
										setEditMode(false);
										setIsLoading(false);
										showSnackbar("Successfully created holiday.", "success");
									}}
									cancelLabel={editMode ? "Cancel" : "Create Holiday"}
									disableSave={addHolidayButtonDisabled}
									saveLabel="Add Holiday"
								/>
							</Grid>
							{editMode && (
								<>
									<Grid item xs={12} md={6}>
										<TextField
											id="standard-basic"
											label="Holiday Name"
											value={holidayName}
											error={errors.holidayName}
											helperText={errors.holidayName ? "A unique holiday name is required." : ""}
											onChange={(event) => {
												setHolidayName(event.target.value);
											}}
											className="w-100"
										/>
									</Grid>
									<Grid item xs={12} md={6} className="mt-2">
										<FormControlLabel
											className="w-100 m-0"
											value="top"
											control={
												<Checkbox
													onChange={(event) => {
														setRepeated(event.target.checked);
														if (isRepeated) {
															let newStartDate = startDate;
															let newEndDate = endDate;
															const currentYear = new Date().getFullYear();
															if (currentYear > newStartDate.getFullYear()) {
																newStartDate.setFullYear(currentYear);
																setStartDate(newStartDate);
															}
															newEndDate.setFullYear(currentYear);
															if (newStartDate > newEndDate) {
																newEndDate.setFullYear(currentYear + 1);
															}
															setEndDate(newEndDate);
														}
													}}
													checked={isRepeated}
												/>
											}
											label="Repeat"
										/>
									</Grid>
									<Grid item xs={12} md={6} className="mt-2">
										<KeyboardDatePicker
											disableToolbar
											variant="inline"
											format={formatString}
											margin="normal"
											id="start-date-picker-inline"
											label="Start Date"
											value={startDate}
											minDate={isRepeated ? null : startOfDayDate}
											minDateMessage="Date cannot be in the past"
											onChange={(newStartDate) => {
												setStartDate(newStartDate);
											}}
											onError={(error) => {
												if (!!error !== errors.startDate) {
													setErrors((err) => ({ ...err, startDate: !!error }));
												}
											}}
											KeyboardButtonProps={{
												"aria-label": "change date",
											}}
											className="w-100 m-0"
										/>
									</Grid>
									<Grid item xs={12} md={6} className="mt-2">
										<KeyboardDatePicker
											disableToolbar
											variant="inline"
											format={formatString}
											margin="normal"
											id="end-date-picker-inline"
											label="End Date"
											value={endDate}
											minDate={isRepeated ? undefined : startDate}
											onChange={setNewEndDate}
											onError={(error) => {
												if (!!error !== errors.endDate) {
													setErrors((err) => ({ ...err, endDate: !!error }));
												}
											}}
											KeyboardButtonProps={{
												"aria-label": "change date",
											}}
											className="w-100 m-0"
										/>
									</Grid>
								</>
							)}
						</Grid>
					</div>
					<List style={{ width: "100%" }}>
						{holidaysToDisplay.length > 0 ? (
							holidaysToDisplay.map((holiday, index) => {
								const deleteHoliday = async () => {
									setConfirmPrompt(
										<ConfirmDialog
											title={`Delete ${holiday.holidayName}`}
											message="Are you sure you want to delete this holiday?"
											confirmOpen
											onClose={async (confirm) => {
												if (confirm) {
													setIsLoading(true);
													await updateHolidaysAsync({ deleteHoliday: holiday }, queue.Id);
													let newHolidaysToDisplay = holidaysToDisplay;
													newHolidaysToDisplay.splice(index, 1);
													setNewHolidaysToDisplay([...newHolidaysToDisplay]);
													setIsLoading(false);
													showSnackbar("Successfully removed holiday.", "success");
												}
												setConfirmPrompt(undefined);
											}}
										></ConfirmDialog>,
									);
								};
								return (
									<React.Fragment key={holiday.holidayName}>
										<Divider />
										<HolidayCard
											setIsLoading={setIsLoading}
											deleteHoliday={deleteHoliday}
											setItemModified={handleItemEditState}
											editHoliday={async (holidayName, startDate, endDate, isRepeated) => {
												const newHolidaysToDisplay = holidaysToDisplay;
												const newHoliday = createHoliday(holidayName, startDate, endDate, queue.Id, isRepeated);
												await updateHolidaysAsync({ updateHoliday: newHoliday, deleteHoliday: holiday }, queue.Id);
												newHolidaysToDisplay[index] = newHoliday;
												setHolidaysToDisplay(newHolidaysToDisplay);
												showSnackbar("Successfully saved holiday.", "success");
											}}
											holiday={holiday}
										/>
									</React.Fragment>
								);
							})
						) : (
							<EmptyPlaceholder message="There are currently no holidays configured for the queue." />
						)}
					</List>
				</div>
			)}
			{!holidaysToDisplay && <ProgressOverlay />}
		</div>
	);
};

export default HolidaysTab;
